import { useQueryClient } from '@tanstack/react-query'
import { api } from '../../../api/client'
import { Button, SectionLabel } from '../../../design/components'
import { jobStatusLine } from '../../jobs/types'
import type { GenerationJob } from '../../jobs/types'
import { useGenerationJob } from '../../jobs/useGenerationJob'
import { sceneKeys } from '../queries'
import type { ScenePage } from '../types'
import styles from './play.module.css'

/**
 * One comic page under a panel, with its generation.
 *
 * A page's image is drawn from the scene's snapshot and the page's own directions, so the
 * API refuses (`409`) when no snapshot has been recorded — which is why the scene's
 * snapshot card sits on the same screen. The failure text says so in the server's own
 * words rather than this component guessing at the cause.
 *
 * Like every image kind, a finished job returns a *pointer* to something already saved:
 * the visible result is the scene refetching and the page reporting `hasImage`.
 */
export function PageRow({
  sceneId,
  page,
  index,
  canGenerate,
}: {
  sceneId: number
  page: ScenePage
  index: number
  canGenerate: boolean
}) {
  const queryClient = useQueryClient()

  const job = useGenerationJob({
    start: () => api.post<GenerationJob>(`/scenes/${sceneId}/panels/pages/${page.id}/generate`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sceneKeys.sheet(sceneId) })
    },
  })

  const thumb = page.imageUrlThumb ?? page.imageUrlSmall ?? page.imageUrl

  return (
    <div className={styles.castRow}>
      <div className={styles.rowHead}>
        <span className={styles.personText}>
          <span className={styles.rowName}>Page {index + 1}</span>
          <span className={styles.rowMeta}>{page.hasImage ? 'Image ready' : 'No image yet'}</span>
        </span>
      </div>

      {page.hasImage && thumb !== null && (
        // The version is what the storage layer bumps on each regeneration, so it is the
        // cache-buster — and using it rather than a timestamp means an unchanged image
        // still comes from cache.
        <img
          className={styles.pageImage}
          src={page.imageVersion === null ? thumb : `${thumb}?v=${page.imageVersion}`}
          alt={`Page ${index + 1}`}
          loading="lazy"
        />
      )}

      {page.directions !== '' && (
        <>
          <SectionLabel tone="muted" micro>
            Directions
          </SectionLabel>
          <p className={styles.rowText}>{page.directions}</p>
        </>
      )}

      {canGenerate && (
        <div className={styles.rowActions}>
          {job.isRunning ? (
            <>
              <span className={styles.rowText} role="status">
                {jobStatusLine(job.job, job.assumedFinished)}
              </span>
              <Button variant="secondary" onClick={job.cancel}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={job.run}>
              {page.hasImage ? 'Regenerate' : 'Generate the image'}
            </Button>
          )}
        </div>
      )}

      {job.failure !== null && <p className={styles.rowText}>{job.failure}</p>}
    </div>
  )
}
